// Expression-context type inference.
//
// `inferExprType` walks any `AstInput` node and returns the
// `ColumnType` it would evaluate to against a given dataset Schema.
// Mirror of `runtime/arquero.compileExpr`'s shape walk, but produces a
// type rather than a per-row function. Consumed by:
//
//   - dataset-consuming ops' `propagateSchema` — e.g. `derive`'s new
//     columns get their types from `inferExprType` on the right-hand
//     expression
//   - the pre-evaluation type-check pass — validates that every
//     expression-context sub-tree types cleanly before any I/O.

import { colNodeLabels } from '../column-ref.ts';
import { ShapeError, UnknownOpError } from '../errors.ts';
import { type AstInput, astShape, INVALID_AST_SHAPE_MESSAGE, nodePos, opCallPos, type Spans } from '../normalize.ts';
import {
  type DatasetCapability,
  type InferFn,
  type NameTable,
  opMeta,
  type PropagateSchemaCtx,
  resolveOpCall,
} from '../op-meta.ts';
import { elemPath } from '../path.ts';
import { Dataset, isUnitCell, type Value } from '../value.ts';
import type { IdentityTable } from './row-identity.ts';
import {
  arrayCol,
  booleanCol,
  type ColumnType,
  dateCol,
  integerCol,
  isNullable,
  nullableCol,
  nullCol,
  numberCol,
  replaceUnit,
  rowIdentityCol,
  type Schema,
  stringCol,
  structPathType,
  unifiedUnitOfTypes,
  unknownCol,
  unwrapNullable,
} from './types.ts';

/** Scalar leaf-type dispatch — null / number / string / boolean. */
const inferPrimitiveType = (value: null | number | string | boolean): ColumnType => {
  if (value === null) {
    return nullCol;
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? integerCol : numberCol;
  }
  if (typeof value === 'string') {
    return stringCol;
  }
  return booleanCol;
};

/**
 * SSOT for the type of an `embedded` AST value — the single arm that types
 * every self-evaluating value (`astShape.kind === 'embedded'`). Order is
 * load-bearing:
 *
 *   1. a value the threaded `IdentityTable` claims → `rowIdentityCol`. This
 *      is where row-identity RECOGNITION lives — identity-ness is a TYPE
 *      question, never asked by the pure-structural `astShape`.
 *   2. null / number / string / boolean → the scalar leaf type.
 *   3. a unit-boxed number (`UnitCell`) → its bare number's leaf type
 *      (units are value-level; the type level never threads them).
 *   4. `Date` → `dateCol`.
 *   5. `Dataset` / `Schema` (a `Map`) — not column values, carry no
 *      column-type meaning → `unknownCol` (downstream treats as a warning).
 *   6. any other (UNCLAIMED) opaque object → a malformed value in
 *      expression position. MALFORMED-VALUE DETECTION lives HERE (it used
 *      to fire in `astShape`'s `invalid` arm): raise the `ShapeError`.
 */
const embeddedValueType = (value: Value, identities: IdentityTable, spans: Spans, path: string): ColumnType => {
  const claimed = identities.claimingIdentity(value);
  if (claimed !== undefined) {
    return rowIdentityCol(claimed);
  }
  if (value === null || typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return inferPrimitiveType(value);
  }
  if (isUnitCell(value)) {
    return inferPrimitiveType(value.value);
  }
  if (value instanceof Date) {
    return dateCol;
  }
  if (value instanceof Dataset || value instanceof Map) {
    return unknownCol;
  }
  throw new ShapeError(INVALID_AST_SHAPE_MESSAGE, nodePos(spans, path));
};

/**
 * Type inference for an `opCall` expression. Two-path dispatch:
 *
 *   - `(col …)` — the canonical row reader `(col % <column> [path…])`.
 *     Best-effort typing: only a row ref (head `%`) is typeable against
 *     the surrounding `schema` — its column is the label after `%`,
 *     typed as that column's declared type; sub-paths walk
 *     `structPathType` (struct fields, or a relation's implicit
 *     instance-identity fields), falling back to `unknownCol` when a
 *     segment doesn't resolve. A foreign / non-`%` head can't be
 *     typed without that dataset's schema, so it is `unknownCol`. The
 *     AUTHORITATIVE row-vs-foreign-vs-unbound decision lives in
 *     `colRefKind` (used by `validateRefs` + the runtime set-ref
 *     rewrite); inference stays conservative. No `inferType` on `col`'s
 *     `expr` capability because the per-op API has no schema access;
 *     the resolution lives here where the closure DOES carry the schema.
 *   - Anything else — delegate to the registered op's `expr.inferType`
 *     (the single source of truth for non-`col` opcalls). Absent →
 *     `unknownCol`; downstream consumers treat `unknown` as a warning,
 *     not a type error.
 */
const inferOpCallType = (
  node: AstInput,
  call: { readonly name: string },
  schema: Schema,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
): ColumnType => {
  if (call.name === 'col') {
    const labels = colNodeLabels(node);
    // Only a canonical row ref `(col % <column> …)` is typeable here:
    // the column is the label after the `%` head. A foreign / non-`%`
    // head (or a Form-2 expression head, `labels === undefined`) needs a
    // schema this layer doesn't have → conservative `unknownCol`.
    if (labels === undefined || labels[0] !== '%' || labels[1] === undefined) {
      return unknownCol;
    }
    const colType = schema.get(labels[1]);
    if (colType === undefined) {
      return unknownCol;
    }
    const subPath = labels.slice(2);
    if (subPath.length === 0) {
      return colType;
    }
    return structPathType(colType, subPath) ?? unknownCol;
  }
  const { meta } = resolveOpCall(call.name, ops, opMeta, opCallPos(spans, path), 'op', UnknownOpError);
  // Gate on the hook's presence, not `evaluation` — mirrors
  // `compileExpr`'s dispatch so a special form that compiles in
  // expression context also type-infers there.
  const inferType = meta.expr?.inferType;
  if (inferType === undefined) {
    return unknownCol;
  }
  const infer: InferFn = (childNode, childPath) =>
    inferExprType(childNode as AstInput, schema, ops, identities, spans, childPath);
  // `inferType`'s stored return widens to `unknown` for the same
  // `$replace` instantiation-depth reason `apply`'s does; cast back to
  // the declared `ColumnType` the op author annotated on the arrow.
  return inferType(call, infer, path) as ColumnType;
};

/**
 * Infer the `ColumnType` of an expression-context AST node.
 *
 *   embedded → see `embeddedValueType` (identity claim → row-identity;
 *     scalar → leaf type; Date → date; Dataset/Schema → unknown; an
 *     unclaimed opaque object raises a `ShapeError`)
 *   vector → array<unified element type>
 *   symbol ref → schema.get(sym) ?? unknown
 *   op call → see `inferOpCallType` (special-cased `(col …)`; otherwise
 *     the op's registered `expr.inferType`)
 */
export const inferExprType = (
  node: AstInput,
  schema: Schema,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
): ColumnType => {
  const shape = astShape(node);
  switch (shape.kind) {
    case 'embedded':
      return embeddedValueType(shape.value, identities, spans, path);
    case 'vector': {
      const elementTypes = shape.elements.map((el, i) =>
        inferExprType(el as AstInput, schema, ops, identities, spans, elemPath(path, i)),
      );
      return arrayCol(unifyColumnTypes(elementTypes));
    }
    case 'symbolRef':
      return schema.get(shape.sym) ?? unknownCol;
    case 'opCall':
      return inferOpCallType(node, shape.call, schema, ops, identities, spans, path);
    // `keyword` / `map` carry no column-type meaning at schema-inference
    // time; downstream type-check treats `unknown` as a warning.
    default:
      return unknownCol;
  }
};

/**
 * Reduce a list of column types to a single common type, absorbing
 * nulls into the `nullable` wrapper. `null` entries and `nullable<T>`
 * wrappers contribute a null flag; the remaining inner types unify by
 * `kind` — all equal ⇒ the first such type (its metadata wins, except
 * the unit hint, which is re-derived via `unifiedUnitOfTypes`: kept only
 * when every contributor agrees, else stripped). When
 * any contributor was null-permitting and the unified inner type is
 * not `unknown`, the result is wrapped as `nullable<...>`. Mixed inner
 * kinds (e.g. `[1 'two' true]`) collapse to `unknown` — the engine
 * does not synthesise union types beyond the `nullable` wrapper. An
 * all-`null` list is `null`; an empty list is `unknown`.
 *
 * Used by vector type inference and if/case/coalesce unification.
 */
export const unifyColumnTypes = (types: readonly ColumnType[]): ColumnType => {
  if (types.length === 0) {
    return unknownCol;
  }
  const inner = types.map(unwrapNullable).filter((t) => t.kind !== 'null');
  const head = inner[0];
  if (head === undefined) {
    return nullCol;
  }
  const unified = inner.every((t) => t.kind === head.kind) ? replaceUnit(head, unifiedUnitOfTypes(inner)) : unknownCol;
  return types.some(isNullable) && unified.kind !== 'unknown' ? nullableCol(unified) : unified;
};

/**
 * Run an op's `propagateSchema` against an input schema, returning the
 * propagated `Schema` or `undefined` when the op's propagator returns
 * undefined (runtime-width ops) or is absent (source ops, which use
 * `sourceSchema` instead — see `DatasetCapability`). Centralises the
 * `PropagateSchemaCtx` construction, the `InferFn` thunk closing over
 * `(inputSchema, ops, identities, spans)`, and the `$output` / `Schema` casts that
 * paper over Zod's registry-stored signature widening — shared between
 * the runtime dispatcher (`evalDatasetOp` in `evaluator.ts`) and the
 * pre-evaluation type-check pass (`inferOpSchema` in
 * `schema/check.ts`).
 *
 * Takes a `DatasetCapability` (not the full op meta) — callers gate
 * on `op.dataset !== undefined` before getting here, so the
 * capability is always present.
 *
 * The `parsed as never` cast matches the `$output` storage marker on
 * `DatasetCapability.propagateSchema`; the `as Schema` cast matches its
 * widened-to-`unknown` return. Both casts live here so the two call
 * sites don't each have to re-explain the ceremony.
 *
 * Throws are NOT caught here — runtime callers let them bubble into
 * `dispatchCall`'s `OpExecutionError` wrapping; the type-check pass
 * wraps its own call in try/catch to stay total.
 */
export const runPropagateSchema = (
  cap: DatasetCapability,
  parsed: unknown,
  inputSchema: Schema,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  path: string,
  liftColumn?: string,
): Schema | undefined => {
  if (cap.propagateSchema === undefined) {
    return undefined;
  }
  const ctx: PropagateSchemaCtx = {
    inputSchema,
    path,
    inferExprType: (node, p, schemaOverride) =>
      inferExprType(node as AstInput, schemaOverride ?? inputSchema, ops, identities, spans, p),
    liftColumn,
  };
  return cap.propagateSchema(parsed as never, ctx) as Schema | undefined;
};
/**
 * Identity propagation: ops that touch rows but never columns
 * (`filter`, `distinct`, `sort`, …) share this `propagateSchema` body.
 * Lives in `schema/infer.ts` (rather than `builtins.ts` where it
 * originated) so op files in `ops/transform/` can import it without
 * transitively importing `builtins.ts` — that import cycle interleaved
 * op-module registration against `builtins.ts`'s module-level
 * initialisation order, producing runtime `undefined` for op-helper
 * symbols.
 *
 * Single source of truth for "schema unchanged after this op" — one
 * edit propagates to every consumer.
 */
export const passThroughSchema = (_data: unknown, ctx: PropagateSchemaCtx): Schema => ctx.inputSchema;
